/**
 * Detection / Decisions - Phase 4 wiring.
 *
 * Live data sources:
 *   GET  /v1/detection/decisions               → list (paginated, cursor-based)
 *   GET  /v1/detection/decisions/:reqId        → single decision detail
 *   GET  /v1/detection/why-blocked/:reqId      → composed reason chain
 *   POST /v1/detection/decisions/:reqId/acknowledge
 *   POST /v1/detection/decisions/:reqId/suppress
 *   POST /v1/detection/decisions/:reqId/kill-session
 *   GET  /v1/detection/shadow-ai/*             → Shadow AI panel (ShadowAiPanel.tsx)
 *
 * The Why-Blocked drawer is the headline demo surface: one Request
 * ID, full reason chain (rule + redundancy votes + per-source verdicts),
 * with operator actions (acknowledge / suppress / kill session / SIEM
 * dispatch) wired to real backends.
 */

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  Gavel, Search as SearchIcon, Cpu, Activity as ActivityIcon, GitBranch,
  Loader2, AlertTriangle, CheckCircle2, X, Zap, ShieldOff,
  Copy, Check, UserCheck, Clock, FileText, Building2, UsersRound,
  Globe, ShieldAlert, Link as LinkIcon,
} from 'lucide-react';
import { GlassCard, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import {
  listDecisions,
  getWhyBlocked,
  killSessionForDecision,
  type Decision,
  type WhyBlockedResponse,
} from '@/lib/api';
import { useCan } from '@/lib/access';
import { acknowledgeDecisionIncident, suppressDecisionIncident } from '@/lib/detection-response-api';
import { ShadowAiPanel } from './ShadowAiPanel';

type DecisionFilter = 'all' | 'alert' | 'block' | 'mask' | 'step_up' | 'allow';

/**
 * The page lists the newest page of decisions plus the newest page of
 * `alert` decisions, fetched on their own (issue #249 round 4). An hourly
 * alert, such as a source whose clock is out of bounds, is written once and
 * then only counted, so on a busy tenant it falls out of the newest 100
 * within seconds; the second request keeps it on the Alerts tab and in All.
 * Issue #314: every consent-bypass read is an `alert` too, so on a busy
 * tenant the hourly alert also falls out of the newest 100 alerts; a third
 * request (`kind=hourly`) keeps the newest 100 hourly alerts on the page.
 */
export function mergeDecisionPages(recent: readonly Decision[], alerts: readonly Decision[], hourly: readonly Decision[] = []): Decision[] {
  const byId = new Map<string, Decision>();
  for (const d of [...recent, ...alerts, ...hourly]) byId.set(d.id, d);
  return [...byId.values()].sort((a, b) => Date.parse(b.decidedAt) - Date.parse(a.decidedAt));
}

/**
 * What raised the decision. A statement read from a database log after it ran
 * (Bank III-49, request id `obs_...`, or `obst_...` for an account's counted
 * truncated statements of one minute; POST /v1/detection/observed-statements)
 * was not stopped, unlike a gateway decision, so the list says so.
 */
export function triggerLabel(d: Pick<Decision, 'triggerType' | 'requestId'>): string {
  if (d.requestId?.startsWith('obs_') || d.requestId?.startsWith('obst_')) return 'observed in log';
  return d.triggerType.replace(/_/g, ' ');
}

/**
 * Badge colour of a decision or vote. Only `allow` is green: an `alert` (Bank
 * III-61, a consent check not seen for an observed read) or any value this
 * list does not know must never read as allowed.
 */
export function decisionBadgeVariant(decision: string): 'critical' | 'warning' | 'info' | 'success' {
  if (decision === 'block' || decision === 'quarantine') return 'critical';
  if (decision === 'mask') return 'info';
  if (decision === 'allow') return 'success';
  return 'warning';
}

const ENGINE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  rule_engine:     Gavel,
  opa_primary:     Gavel,
  opa_shadow:      Cpu,
  rule_engine_v1:  Cpu,
  ueba:            ActivityIcon,
  policy_graph:    GitBranch,
};

// F-4.3 - per-source icons for the Why-Blocked reason chain. New
// sources land alongside rule_engine (added Day 2).
const REASON_SOURCE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  rule_engine:   Gavel,
  consent:       UserCheck,
  retention:     Clock,
  dpa_on_file:   FileText,
  bu_owner:      Building2,
  manager_chain: UsersRound,
  cross_border:  Globe,
  section_9:     ShieldAlert,
  ueba_zscore:   ActivityIcon,
  linked_record: LinkIcon,
};

function timeStr(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', { hour12: false });
}

export function DetectionDecisions() {
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<DecisionFilter>('all');
  // P1-5 (2026-05-26) - read ?q= from URL on mount so deep-links from
  // UEBA / Sonar / Pwave findings pre-filter the decision list.
  const [search, setSearch] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('q') ?? '';
  });
  const [triggerFilter, setTriggerFilter] = useState<string>('all');
  const [selectedReqId, setSelectedReqId] = useState<string | null>(null);
  const [whyBlocked, setWhyBlocked] = useState<WhyBlockedResponse | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<'ack' | 'suppress' | 'kill' | null>(null);
  const [actionResult, setActionResult] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const canApproveAiTools = useCan('detection:rule:approve');

  const refresh = (signal?: AbortSignal) =>
    Promise.all([listDecisions(), listDecisions({ decision: 'alert' }), listDecisions({ decision: 'alert', kind: 'hourly' })])
      .then(
        ([recent, alerts, hourly]) =>
          !signal?.aborted && setDecisions(mergeDecisionPages(recent.decisions, alerts.decisions, hourly.decisions)),
      )
      .catch((err) => !signal?.aborted && setError(err instanceof Error ? err.message : 'load failed'))
      .finally(() => !signal?.aborted && setLoading(false));

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  // F-4.3 - auto-open drawer when the URL carries a Request ID. Supports
  // both `?reqId=BLK-…` (F-7.2 notifications use this) and the path
  // segment `/detection/decisions/BLK-…` (manual deep-link). On match,
  // setSelectedReqId triggers the existing why-blocked fetch effect.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sp = new URLSearchParams(window.location.search);
    const fromQuery = sp.get('reqId');
    // Path-segment form: pull the last segment after /decisions/
    const path = window.location.pathname;
    const match = path.match(/\/detection\/decisions\/(BLK-[A-Z0-9]+)\/?$/);
    const fromPath = match?.[1] ?? null;
    const deepLinkId = fromQuery || fromPath;
    if (deepLinkId) {
      setSelectedReqId(deepLinkId);
    }
  }, []);

  // F-4.3 - Copy Request ID to clipboard with a 1.5s confirmation.
  async function onCopyRequestId(reqId: string) {
    try {
      await navigator.clipboard.writeText(reqId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Older browsers / insecure contexts - fall through silently.
    }
  }

  // Why-Blocked fetch when a decision is selected
  useEffect(() => {
    if (!selectedReqId) {
      setWhyBlocked(null);
      setDrawerError(null);
      return;
    }
    const ctrl = new AbortController();
    setDrawerLoading(true);
    setDrawerError(null);
    setActionResult(null);
    getWhyBlocked(selectedReqId)
      .then((r) => !ctrl.signal.aborted && setWhyBlocked(r))
      .catch((err) => !ctrl.signal.aborted && setDrawerError(err instanceof Error ? err.message : 'load failed'))
      .finally(() => !ctrl.signal.aborted && setDrawerLoading(false));
    return () => ctrl.abort();
  }, [selectedReqId]);

  const triggerTypes = useMemo(() => {
    const set = new Set<string>();
    for (const d of decisions) set.add(d.triggerType);
    return Array.from(set).sort();
  }, [decisions]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return decisions.filter((d) => {
      if (filter !== 'all' && d.decision !== filter) return false;
      if (triggerFilter !== 'all' && d.triggerType !== triggerFilter) return false;
      if (needle) {
        const hay = `${d.requestId ?? ''} ${d.id} ${d.decisionExplanation ?? ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [decisions, filter, search, triggerFilter]);

  const counts = useMemo(() => ({
    all: decisions.length,
    alert:   decisions.filter((d) => d.decision === 'alert').length,
    block:   decisions.filter((d) => d.decision === 'block').length,
    step_up: decisions.filter((d) => d.decision === 'step_up').length,
    mask:    decisions.filter((d) => d.decision === 'mask').length,
    allow:   decisions.filter((d) => d.decision === 'allow').length,
  }), [decisions]);

  async function onAck(reqId: string) {
    setActionBusy('ack');
    try {
      // Q35: acknowledges the decision's incident, with owner and time.
      const r = await acknowledgeDecisionIncident(reqId);
      setActionResult(`Incident acknowledged by you at ${new Date(r.incident.acknowledgedAt ?? Date.now()).toLocaleString('en-IN')}. Act on it on the Incidents page.`);
    } catch (err) {
      setActionResult(`Failed: ${err instanceof Error ? err.message : 'unknown'}`);
    } finally {
      setActionBusy(null);
    }
  }

  async function onSuppress(reqId: string) {
    // Q35: a reason and an expiry are required; from here the scope is this
    // actor on this store for one day (the Incidents page offers the others).
    const reason = window.prompt('Why suppress this actor on this store for one day? (recorded in the audit trail)', 'False positive');
    if (!reason || reason.trim().length < 3) return;
    setActionBusy('suppress');
    try {
      const r = await suppressDecisionIncident(reqId, { scope: 'actor_store', reason: reason.trim(), expiresInHours: 24 });
      setActionResult(`Suppressed this actor on this store until ${new Date(r.suppression.expiresAt).toLocaleString('en-IN')}. Later hits are still counted on the Incidents page.`);
    } catch (err) {
      setActionResult(`Failed: ${err instanceof Error ? err.message : 'unknown'}`);
    } finally {
      setActionBusy(null);
    }
  }

  async function onKill(reqId: string) {
    if (!window.confirm('Kill the actor\'s active session?\nThis is a hard enforcement action.')) return;
    setActionBusy('kill');
    try {
      const r = await killSessionForDecision(reqId);
      setActionResult(`Kill dispatched by ${r.killedBy}. ${r.note}`);
    } catch (err) {
      setActionResult(`Failed: ${err instanceof Error ? err.message : 'unknown'}`);
    } finally {
      setActionBusy(null);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Decisions"
        subtitle="Every DDR decision with full triple-redundancy vote breakdown. Click any row to see the Why-Blocked reason chain for that Request ID."
        badges={[
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [
                  { label: `${counts.all} total`, variant: 'glass' as const },
                  ...(counts.block > 0 ? [{ label: `${counts.block} blocked`, variant: 'critical' as const }] : []),
                  ...(counts.alert > 0 ? [{ label: `${counts.alert} alerts`, variant: 'warning' as const }] : []),
                ]),
        ]}
      />

      {loading && (
        <LoadingState caption="Loading decisions…" />
      )}
      {error && !loading && (
        <ErrorState
          title="Failed to load decisions"
          detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }}
        />
      )}

      {!loading && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Tabs value={filter} onValueChange={(v) => setFilter(v as DecisionFilter)}>
              <TabsList>
                <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
                <TabsTrigger value="alert">Alerts ({counts.alert})</TabsTrigger>
                <TabsTrigger value="block">Blocked ({counts.block})</TabsTrigger>
                <TabsTrigger value="step_up">Step-up ({counts.step_up})</TabsTrigger>
                <TabsTrigger value="mask">Masked ({counts.mask})</TabsTrigger>
                <TabsTrigger value="allow">Allowed ({counts.allow})</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex items-center gap-2 ml-auto">
              <div className="relative">
                <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filter by Request ID or text…"
                  className="glass-lite rounded-md pl-7 pr-2 py-1.5 text-xs w-64 outline-none focus:border-primary/40 border border-border/40"
                />
              </div>
              {triggerTypes.length > 1 && (
                <select
                  value={triggerFilter}
                  onChange={(e) => setTriggerFilter(e.target.value)}
                  className="glass-lite rounded-md px-2 py-1.5 text-xs border border-border/40 outline-none focus:border-primary/40"
                  title="Filter by trigger type"
                >
                  <option value="all">All triggers</option>
                  {triggerTypes.map((t) => (
                    <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              )}
              {(search || triggerFilter !== 'all') && (
                <Button
                  size="sm"
                  variant="glass"
                  onClick={() => { setSearch(''); setTriggerFilter('all'); }}
                  title="Clear filters"
                >
                  <X className="h-3 w-3" /> Clear
                </Button>
              )}
            </div>
          </div>

          {filtered.length === 0 ? (
            <GlassCard className="p-8 text-center">
              <Gavel className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <CardTitle className="text-base">No decisions for this filter</CardTitle>
              <CardDescription className="text-sm mt-1">
                Decisions are recorded by the rule engine as queries and findings flow through. Try widening the filter or seeding demo data.
              </CardDescription>
            </GlassCard>
          ) : (
            <GlassCard className="p-0 overflow-hidden">
              <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40 font-medium gap-3">
                <div className="col-span-1">Time</div>
                <div className="col-span-1">Decision</div>
                <div className="col-span-3">Request ID</div>
                <div className="col-span-2">Trigger</div>
                <div className="col-span-5">Explanation</div>
              </div>
              {filtered.slice(0, 200).map((d, i) => (
                <motion.button
                  key={d.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.01, 0.3) }}
                  onClick={() => setSelectedReqId(d.requestId ?? d.id)}
                  className={cn(
                    'w-full grid grid-cols-12 gap-3 px-4 py-3 text-left text-sm border-b border-border/30 hover:bg-muted/40 transition-colors',
                    d.decision === 'block' && 'bg-sev-critical/5',
                  )}
                >
                  <span className="col-span-1 font-mono text-[10px] text-muted-foreground">
                    {timeStr(d.decidedAt)}
                  </span>
                  <Badge
                    variant={decisionBadgeVariant(d.decision)}
                    className="col-span-1 text-[9px] justify-center"
                  >
                    {d.decision}
                  </Badge>
                  <code className="col-span-3 font-mono text-[11px] truncate">
                    {d.requestId ?? d.id.slice(0, 18) + '…'}
                  </code>
                  <code className="col-span-2 font-mono text-[11px] capitalize text-muted-foreground">
                    {triggerLabel(d)}
                  </code>
                  <span className="col-span-5 text-xs text-muted-foreground truncate">
                    {d.decisionExplanation ?? '-'}
                  </span>
                </motion.button>
              ))}
            </GlassCard>
          )}
        </>
      )}

      <ShadowAiPanel canApprove={canApproveAiTools} onOpenDecision={setSelectedReqId} />

      {/* Why-Blocked drawer */}
      {selectedReqId && (
        <div
          className="fixed inset-0 z-50 bg-background/60 backdrop-blur-sm flex justify-end"
          onClick={() => setSelectedReqId(null)}
        >
          <div
            className="h-full w-full max-w-[720px] glass-strong border-l border-border/40 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center gap-2 px-5 py-3 border-b border-border/40 glass-strong">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Why-Blocked</div>
                <div className="text-sm font-mono truncate">{selectedReqId}</div>
              </div>
              <Button variant="glass" size="sm" onClick={() => setSelectedReqId(null)}><X className="h-4 w-4" /></Button>
            </div>

            <div className="p-5 space-y-4 text-sm">
              {drawerLoading && (
                <div className="flex items-center gap-2 text-muted-foreground text-xs py-8 justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading Why-Blocked…
                </div>
              )}
              {drawerError && !drawerLoading && (
                <div className="glass-lite border border-warning/40 rounded-md p-3 text-xs flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium">Failed to load reason chain</div>
                    <div className="text-muted-foreground mt-0.5">{drawerError}</div>
                  </div>
                </div>
              )}
              {whyBlocked && !drawerLoading && (
                <>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Decision</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge
                        variant={decisionBadgeVariant(whyBlocked.decision)}
                        className="capitalize"
                      >
                        {whyBlocked.decision}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">
                        decided at <code className="font-mono">{new Date(whyBlocked.decidedAt).toLocaleString()}</code>
                      </span>
                    </div>
                  </div>

                  {whyBlocked.ruleName && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Triggering rule</div>
                      <div className="flex items-center gap-2">
                        <ScrollText className="h-3.5 w-3.5 text-muted-foreground" />
                        <code className="text-xs font-mono">{whyBlocked.ruleName}</code>
                        {whyBlocked.ruleSeverity && (
                          <Badge variant={whyBlocked.ruleSeverity as 'critical' | 'high' | 'medium' | 'low'} className="text-[9px]">
                            {whyBlocked.ruleSeverity}
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {whyBlocked.explanation && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Explanation</div>
                      <p className="text-xs leading-relaxed text-foreground/90 whitespace-pre-wrap">{whyBlocked.explanation}</p>
                    </div>
                  )}

                  {whyBlocked.redundancy.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Triple-redundancy votes</div>
                      <div className="space-y-1.5">
                        {whyBlocked.redundancy.map((v, i) => {
                          const Icon = ENGINE_ICON[v.engine] ?? Cpu;
                          return (
                            <div key={i} className="glass-lite rounded-md p-2.5 flex items-center gap-3">
                              <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <code className="text-[11px] font-mono">{v.engine}</code>
                              <Badge variant={decisionBadgeVariant(v.vote)} className="text-[9px] capitalize">
                                {v.vote}
                              </Badge>
                              <span className="ml-auto text-[10px] tabular text-muted-foreground">
                                {(v.confidence * 100).toFixed(0)}% conf.
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {whyBlocked.reasons.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Reason chain</div>
                      <ol className="space-y-1.5">
                        {whyBlocked.reasons.map((r, i) => {
                          const SourceIcon = REASON_SOURCE_ICON[r.source] ?? Cpu;
                          return (
                            <li key={i} className="glass-lite rounded-md p-2.5 text-xs flex items-start gap-2">
                              <SourceIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <div className="font-mono text-[10px] text-muted-foreground">{r.source}</div>
                                <div className="text-foreground/90 mt-0.5">{r.detail}</div>
                              </div>
                              <Badge variant="glass" className="text-[9px] capitalize">{r.verdict}</Badge>
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  )}

                  {/* F-4.3 - Synthetic-Error Contract: one Request ID
                      flows from SQL error → decision → audit chain →
                      SIEM ticket → this screen. Copy-button for SOC. */}
                  <div className="border-t border-border/30 pt-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Request ID (Synthetic-Error Contract)</div>
                    <div className="flex items-center gap-2">
                      <code className="text-[11px] font-mono flex-1 truncate select-all">{whyBlocked.requestId}</code>
                      <Button
                        size="sm"
                        variant="glass"
                        onClick={() => onCopyRequestId(whyBlocked.requestId)}
                        title="Copy to clipboard"
                      >
                        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                        {copied ? 'Copied' : 'Copy'}
                      </Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Paste this into the SIEM ticket / Jira / call. Same ID appears in the SQL error, audit chain entry, and SOC alert.
                    </p>
                  </div>

                  {/* Operator actions */}
                  <div className="pt-3 border-t border-border/30">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Operator actions</div>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Allowed and masked decisions are never incidents: nothing to acknowledge (review at abfa9c06). */}
                      {whyBlocked.decision !== 'allow' && whyBlocked.decision !== 'mask' && (
                      <Button
                        size="sm"
                        variant="glass"
                        onClick={() => onAck(whyBlocked.requestId)}
                        disabled={actionBusy !== null}
                        data-action="disc.ddr_decisions.a01"
                      >
                        {actionBusy === 'ack' ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                        Acknowledge
                      </Button>
                      )}
                      <Button
                        size="sm"
                        variant="glass"
                        onClick={() => onSuppress(whyBlocked.requestId)}
                        disabled={actionBusy !== null}
                        data-action="disc.ddr_decisions.a02"
                      >
                        {actionBusy === 'suppress' ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldOff className="h-3 w-3" />}
                        Suppress
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => onKill(whyBlocked.requestId)}
                        disabled={actionBusy !== null}
                        className="col-span-2"
                        title="Terminate the actor's active session (audit-chained)"
                        data-action="disc.ddr_decisions.a03"
                      >
                        {actionBusy === 'kill' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                        Kill session
                      </Button>
                    </div>
                    {actionResult && (
                      <div className="glass-lite rounded-md p-2 mt-2 text-[11px] text-muted-foreground">
                        {actionResult}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
}

// Inline import to avoid pulling lucide-react ScrollText twice
function ScrollText(props: { className?: string }) {
  return <Gavel {...props} />;
}
